<template>
  <FormField
    v-if="field.mainValueKey"
    ref="fieldRef"
    v-model="formData[field.id]"
    v-model:main-value="formData[field.mainValueKey]"
    :class="{ dense }"
    :dense="dense"
    :errors="getLabelledErrors(field)"
    :field="parsedFieldConfig"
    :rounded="dense ? 0 :undefined"
    :rules="formRulesMap[field.id]"
    v-bind="$attrs"
  />

  <FormField
    v-else
    ref="fieldRef"
    v-model="formData[field.id]"
    v-model:main-value="neverData[neverKey]"
    :class="{ dense }"
    :dense="dense"
    :errors="formErrors[field.id]"
    :field="parsedFieldConfig"
    :rounded="dense ? 0 :undefined"
    :rules="formRulesMap[field.id]"
    v-bind="$attrs"
  />
</template>

<script setup lang="ts">
// eslint-disable-next-line @typescript-eslint/consistent-type-imports
import type { FormField } from '#components';

const props = defineProps<{
  field: FieldConfig
  data: GenericFormData
  rules: FormRules
  errors: FormErrors
  dense?: boolean
}>();

const fieldRef = ref<InstanceType<typeof FormField>>();
const formData = toRef(props, 'data');
const formErrors = toRef(props, 'errors');
const formRulesMap = toRef(props, 'rules');
const neverKey = '__never__';
const neverData = { [neverKey]: '' };

const getLabelledErrors = (field: FieldConfig) => {
  const fieldErrors = (formErrors.value[field.id] || []);
  const mainValueLabel = humanizeSnakeCase(field.mainValueKey!);
  const mainValueErrors = (formErrors.value[field.mainValueKey!] || []);
  return [...fieldErrors, ...mainValueErrors.map(err => `${mainValueLabel}: ${err}`)];
};

const isFunction = <F = (data: GenericFormData) => any>(x: unknown): x is F => {
  return typeof x === 'function';
};

type NotFunction<T> = T extends (...args: any[]) => any ? never : T;
const parseFieldConfigProp = <T, F = (data: GenericFormData) => T>(p: NotFunction<T> | F, formData: GenericFormData) => {
  if (isFunction(p)) {
    return p(formData);
  }
  return p;
};

const parsedFieldConfig = computed(() => {
  return {
    ...props.field,
    readonly: parseFieldConfigProp(props.field.readonly, formData.value),
  };
});

const reset = () => fieldRef.value?.reset();

defineExpose({ reset });
</script>
